import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const requests = new Map();
const API_BASE = process.env.REACT_APP_BACKEND_URL;

function load(path) {
  const cached = requests.get(path);
  if (cached && Date.now() - cached.at < 15000) return cached.promise;
  const promise = fetch(`${API_BASE}/api/seo/page?compact=true&path=${encodeURIComponent(path)}`, { redirect: "manual" })
    .then(async (r) => {
      if (r.status >= 500 || r.status === 0) throw new Error("SEO temporarily unavailable");
      const page = await r.json();
      if (![200, 404].includes(page.status)) throw new Error("SEO response not a page");
      return page;
    }).catch((error) => { requests.delete(path); throw error; });
  requests.set(path, { at: Date.now(), promise });
  return promise;
}

function apply(page) {
  document.title = page.title;
  // Clear every previous owner, including old static-template JSON-LD and canonical.
  document.querySelectorAll('link[rel="canonical"], script[type="application/ld+json"]').forEach((el) => el.remove());
  document.querySelectorAll('meta[name="description"], meta[name="robots"], meta[property^="og:"], meta[name^="twitter:"]').forEach((el) => el.remove());
  const meta = (attr, key, value) => {
    if (!value) return;
    const el = document.createElement("meta"); el.setAttribute(attr, key); el.content = value; document.head.appendChild(el);
  };
  meta("name", "description", page.description);
  meta("name", "robots", page.robots);
  meta("property", "og:title", page.title);
  meta("property", "og:description", page.description);
  meta("property", "og:url", page.canonical);
  meta("property", "og:type", page.ogType || "website");
  meta("property", "og:image", page.image);
  meta("property", "og:site_name", "PX Group");
  meta("property", "og:locale", "it_IT");
  meta("name", "twitter:card", page.image ? "summary_large_image" : "summary");
  meta("name", "twitter:title", page.title);
  meta("name", "twitter:description", page.description);
  meta("name", "twitter:image", page.image);
  if (page.canonical) {
    const link = document.createElement("link"); link.rel = "canonical"; link.href = page.canonical; document.head.appendChild(link);
  }
  if (page.schema) {
    const script = document.createElement("script"); script.id = "seo-jsonld"; script.type = "application/ld+json";
    script.textContent = JSON.stringify(page.schema); document.head.appendChild(script);
  }
}

/** Metadata is owned by the server registry, never by component loading/error guesses. */
export default function useSEO() {
  const { pathname } = useLocation();
  useEffect(() => {
    let cancelled = false;
    const initial = window.__PX_SEO__;
    if (initial?.path === pathname && [200, 404].includes(initial.status)) {
      if (!Object.prototype.hasOwnProperty.call(initial, 'schema')) {
        try { initial.schema = JSON.parse(document.getElementById('seo-jsonld')?.textContent || 'null'); }
        catch { initial.schema = null; }
      }
      apply(initial);
      return () => { cancelled = true; };
    }
    else {
      // Do not carry a preceding 404/noindex or homepage canonical to a new route.
      document.querySelectorAll('meta[name="robots"], link[rel="canonical"], script[type="application/ld+json"]').forEach((el) => el.remove());
    }
    load(pathname).then((page) => { if (!cancelled) apply(page); }).catch(() => {
      // A temporary API failure is NOT evidence that a page doesn't exist.
      // Keep same-route SSR metadata; do not synthesize noindex or a false 404.
    });
    return () => { cancelled = true; };
  }, [pathname]);
}